import { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";

import { useLazyQuery } from "@stadium/services/apollo-client";
import { useWeglot } from "@stadium/services/weglot/provider";
import { camelizer } from "@stadium/helpers/formatter";

import { getIsBundleShop } from "@selectors/store";

import { useSendItemsContext } from "@contexts/send-items-context";

import { useFetchInventoryDetails } from "@hooks/product-detail/use-fetch-inventory";
import useDraftCatalogSelectedProductsPresence from "@hooks/automated-catalog/use-draft-catalog-selected-products-presence";
import { fetchBaseProductsList } from "@graphql/queries/stores/products/fetch-base-products-list";
import { searchProductsSerializer } from "@serializers/search-products";
import { useCreateSwagContext } from "@contexts/create-swag-context";

const emptyArr = [];

const useFetchBaseProductList = ({ skipInventoryFetching = true } = {}) => {
  const [fetchedProducts, setFetchedProducts] = useState([]);
  const { fetchInventoryDetails, isInventoryFetching, variantData } = useFetchInventoryDetails();
  const { getTranslation } = useWeglot();
  const { isSwagCreationShopFlow } = useCreateSwagContext();

  const { volumeDiscountProductsVariables } = useSendItemsContext();

  const isKitShop = useSelector(getIsBundleShop);

  const [totalProducts, setTotalProducts] = useState(0);
  const [hasDataFetched, setHasDataFetched] = useState(false);

  const [fetchProductList, { data, loading }] = useLazyQuery(fetchBaseProductsList, {
    fetchPolicy: "cache-and-network",
  });

  const productListOptionsRef = useRef({});
  productListOptionsRef.current.getTranslation = getTranslation;
  productListOptionsRef.current.isKitShop = isKitShop;
  productListOptionsRef.current.volumeDiscountProductsVariables = volumeDiscountProductsVariables;

  const { fetchSelectedProductsPresence } = useDraftCatalogSelectedProductsPresence();

  const getProductList = useCallback(async (options, ...args) => {
    const { getTranslation, isKitShop, volumeDiscountProductsVariables } =
      productListOptionsRef.current;
    const { variables } = options;
    const { searchQuery } = variables;
    const translatedQuery = await getTranslation({ text: searchQuery });

    return fetchProductList(
      {
        ...options,
        variables: {
          includeProductVariants: isKitShop,
          ...variables,
          searchQuery: translatedQuery,
          ...volumeDiscountProductsVariables,
        },
      },
      ...args,
    );
  }, []);

  useEffect(() => {
    if (fetchedProducts.length && !skipInventoryFetching && !isInventoryFetching) {
      fetchInventoryDetails(fetchedProducts);
    }
  }, [fetchInventoryDetails, fetchedProducts, skipInventoryFetching]);

  useEffect(() => {
    if (data) {
      const products = searchProductsSerializer(data?.baseSearch?.products || emptyArr);
      setFetchedProducts(products);
      setTotalProducts(data?.baseSearch?.total || 0);
      if (isSwagCreationShopFlow && products.length) {
        const productIds = products.map(({ productId }) => Number(productId)).filter(Boolean);
        fetchSelectedProductsPresence(productIds);
      }
      setHasDataFetched(true);
    } else setHasDataFetched(false);
  }, [data, setFetchedProducts, setTotalProducts]);

  const aggregations = data?.baseSearch?.aggregations;
  const updatedAggregations = camelizer(data?.baseSearch?.aggregations);
  return {
    loading: loading || !hasDataFetched,
    fetchedProducts,
    totalProducts,
    aggregations,
    updatedAggregations,
    getProductList,
    hasDataFetched,
    productsInventory: variantData,
    isInventoryFetching,
  };
};

export default useFetchBaseProductList;
